'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Clip } from '@/lib/supabase';

const PARAM = 'clip';

function readClipParam(): string | null {
  if (typeof window === 'undefined') return null;
  return new URL(window.location.href).searchParams.get(PARAM);
}

function urlWithClip(clipId: string | null): string {
  const url = new URL(window.location.href);
  if (clipId) url.searchParams.set(PARAM, clipId);
  else url.searchParams.delete(PARAM);
  return url.pathname + url.search + url.hash;
}

/**
 * Open-clip state for a profile's clip viewer, mirrored into `?clip=<id>` so
 * a clip link can be shared and Back closes the viewer.
 *
 * Opening pushes one history entry; moving between clips replaces it (Back
 * closes the viewer instead of stepping through every clip watched). Reads
 * `window.location` directly rather than `useSearchParams` so the ISR public
 * profile page stays static.
 */
export function useClipViewerUrl(clips: Clip[]) {
  const [index, setIndex] = useState<number | null>(null);
  // True while the open viewer came from a shared `?clip=` link (analytics).
  const [deepLinked, setDeepLinked] = useState(false);
  // True when the current ?clip entry was pushed by us, so closing can pop it.
  const pushedRef = useRef(false);
  const clipsRef = useRef(clips);
  clipsRef.current = clips;
  const syncedFromLoadRef = useRef(false);

  // Deep link: open the clip named in the URL once the clips are known.
  useEffect(() => {
    if (syncedFromLoadRef.current || clips.length === 0) return;
    syncedFromLoadRef.current = true;
    const id = readClipParam();
    if (!id) return;
    const i = clips.findIndex((c) => c.id === id);
    if (i >= 0) {
      setDeepLinked(true);
      setIndex(i);
    } else window.history.replaceState(window.history.state, '', urlWithClip(null));
  }, [clips]);

  // Keep the index valid if the list changes underneath (e.g. owner deletes a clip).
  useEffect(() => {
    if (index !== null && index >= clips.length) {
      setIndex(clips.length > 0 ? clips.length - 1 : null);
    }
  }, [clips.length, index]);

  useEffect(() => {
    const onPopState = () => {
      const id = readClipParam();
      const i = id ? clipsRef.current.findIndex((c) => c.id === id) : -1;
      if (i >= 0) {
        // Forward into an entry we created earlier.
        pushedRef.current = true;
        setDeepLinked(false);
        setIndex(i);
      } else {
        pushedRef.current = false;
        setIndex(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const open = useCallback((i: number) => {
    const clip = clipsRef.current[i];
    if (!clip) return;
    if (readClipParam()) {
      window.history.replaceState(window.history.state, '', urlWithClip(clip.id));
    } else {
      window.history.pushState(null, '', urlWithClip(clip.id));
      pushedRef.current = true;
    }
    setDeepLinked(false);
    setIndex(i);
  }, []);

  const go = useCallback((i: number) => {
    const clip = clipsRef.current[i];
    if (!clip) return;
    window.history.replaceState(window.history.state, '', urlWithClip(clip.id));
    setIndex(i);
  }, []);

  const close = useCallback(() => {
    setIndex(null);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    } else if (readClipParam()) {
      window.history.replaceState(window.history.state, '', urlWithClip(null));
    }
  }, []);

  return { index, deepLinked, open, go, close };
}
